import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "disabled", "duration", "failed", "loading", "onClick", "onLoadingFinish", "preventClicksOnLoading", "keepResultAfterFinish", "resultIcon", "size", "spinnerProps", "truncateTooltip", "use", "onFocus", "onBlur", "autoFocus", "type", "responsive", "href", "external", "form", "buttonRef", "id", "block", "onMouseEnter", "aria-label", "onMouseLeave", "rel", "name", "truncate", "target", "selected", "role", "download", "title", "onKeyDown", "truncateStringProps", "readOnly", "value", "iconProps"];
import { Component } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { Button } from '../Button/Button';
import { renderChildren } from '../Button/Button.utils';
import { LoadingSpinner, SpinnerOuter } from '../LoadingSpinner/LoadingSpinner';
import { callIfPossible } from '../../utils/functions/functions';
import { SuccessIcon } from 'foundations-assets';
import styled from 'foundations-theming/utils';
import { LoadingButtonProvider } from './LoadingButtonProvider';
import { getSharedButtonContainerStyles } from '../Button/Button.helpers';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const StyledLoadingButtonContent = styled.span.withConfig({
  displayName: "LoadingButton__StyledLoadingButtonContent",
  componentId: "fsyvsf-0"
})(({
  $isLoading,
  theme
}) => {
  return Object.assign({}, getSharedButtonContainerStyles(theme), {
    opacity: $isLoading ? 0 : 1,
    transition: 'opacity .2s cubic-bezier(.42, 0, .58, 1)',
    justifyContent: 'center'
  });
});
export class LoadingButton extends Component {
  static getDerivedStateFromProps(props, state) {
    const {
      prevLoading
    } = state;
    const {
      failed = false,
      loading = false,
      resultIcon = SuccessIcon
    } = props;
    const hasResult = resultIcon != null && !failed;
    const shouldDisplayResult = hasResult && prevLoading && !loading;
    if (loading !== prevLoading) {
      return {
        isLoading: loading,
        prevLoading: loading,
        displayResult: shouldDisplayResult
      };
    }
    return null;
  }
  constructor(props) {
    super(props);
    this.componentDidUpdate = (prevProps, prevState) => {
      const {
        duration
      } = this.props;
      const {
        isLoading: wasLoading
      } = prevState;
      const {
        isLoading
      } = this.state;
      const startedLoading = !wasLoading && isLoading;
      if (duration && startedLoading) {
        clearTimeout(this.loadingTimeout);
        this.loadingTimeout = setTimeout(this.handleLoadingFinish, duration);
      }
    };
    this.handleLoadingFinish = () => {
      const {
        failed = false,
        resultIcon = SuccessIcon
      } = this.props;
      const hasResult = resultIcon != null && !failed;
      this.setState({
        isLoading: false,
        displayResult: hasResult
      });
      if (!hasResult) {
        this.setDisplayResult();
      }
    };
    this.setDisplayResult = () => {
      const {
        onLoadingFinish = emptyFunction,
        keepResultAfterFinish = false
      } = this.props;
      this.setState({
        displayResult: keepResultAfterFinish
      });
      onLoadingFinish();
    };
    this.handleClick = evt => {
      const {
        duration,
        onClick = () => {},
        preventClicksOnLoading = true
      } = this.props;
      const {
        displayResult,
        isLoading
      } = this.state;
      if (preventClicksOnLoading && (isLoading || displayResult)) {
        evt.preventDefault();
        return;
      }
      callIfPossible(onClick, evt);
      if (evt.defaultPrevented) {
        return;
      }
      if (!isLoading && duration) {
        this.setState({
          isLoading: true
        });
      }
    };
    this.loadingTimeout = undefined;
    const {
      loading = false
    } = props;
    this.state = {
      displayResult: false,
      isLoading: loading || false,
      prevLoading: loading
    };
  }
  componentDidMount() {
    const {
      duration
    } = this.props;
    const {
      isLoading
    } = this.state;
    if (duration && isLoading) {
      this.loadingTimeout = setTimeout(this.handleLoadingFinish, duration);
    }
  }
  componentWillUnmount() {
    clearTimeout(this.loadingTimeout);
  }
  render() {
    const _this$props = this.props,
      {
        children,
        disabled = false,
        failed = false,
        loading: __loading = false,
        onClick: __onClick = () => {},
        onLoadingFinish: __onLoadingFinish = emptyFunction,
        preventClicksOnLoading: __preventClicksOnLoading = true,
        keepResultAfterFinish: __keepResultAfterFinish = false,
        resultIcon = SuccessIcon,
        size = 'medium',
        spinnerProps,
        truncateTooltip,
        use = 'secondary',
        onFocus,
        onBlur,
        autoFocus,
        type,
        responsive,
        href,
        external,
        form,
        buttonRef,
        id,
        block,
        onMouseEnter,
        'aria-label': ariaLabel,
        onMouseLeave,
        rel,
        name,
        truncate,
        target,
        selected,
        role,
        download,
        title,
        onKeyDown,
        truncateStringProps,
        readOnly,
        value,
        iconProps
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      isLoading,
      displayResult
    } = this.state;
    const disabledUnlessLoading = disabled && !isLoading && !displayResult;
    let loadingState = 'idle';
    if (isLoading) {
      loadingState = 'loading';
    }
    if (displayResult) {
      loadingState = 'success';
    }

    // this wraps any bare strings within a <span> to provide a more predictable DOM for applying automatic spacing rules.
    const renderedContents = renderChildren(children);
    const getButtonStyleOverrides = theme => {
      return Object.assign({
        position: 'relative',
        overflow: 'visible'
      }, (isLoading || displayResult) && use !== 'secondary-ghost' ? {
        backgroundColor: `${theme.color['fill-surface-default']} !important`
      } : undefined, {
        [`& ${SpinnerOuter}`]: {
          insetInlineStart: '50%',
          insetBlockStart: '50%',
          marginBlock: theme.space['0'],
          marginInline: theme.space['0'],
          opacity: isLoading || displayResult ? '1' : '0',
          position: 'absolute',
          transform: 'translate(-50%, -50%) scale(0.7)',
          transition: 'opacity .2s cubic-bezier(.42, 0, .58, 1)',
          // Overrides SVG width and height in `Button`
          '& svg[data-icon-name]': {
            width: 'initial',
            height: 'initial'
          }
        }
      });
    };
    return /*#__PURE__*/_jsx(LoadingButtonProvider, {
      getButtonStyleOverrides: getButtonStyleOverrides,
      children: /*#__PURE__*/_jsxs(Button, Object.assign({}, rest, {
        size: size,
        use: use,
        "data-loading": isLoading,
        "data-loading-state": loadingState,
        disabled: disabledUnlessLoading,
        onClick: this.handleClick,
        onFocus: onFocus,
        onBlur: onBlur,
        autoFocus: autoFocus,
        truncateTooltip: truncateTooltip || children,
        type: type,
        responsive: responsive,
        href: href,
        external: external,
        form: form,
        buttonRef: buttonRef,
        id: id,
        block: block,
        onMouseEnter: onMouseEnter,
        "aria-label": ariaLabel,
        onMouseLeave: onMouseLeave,
        rel: rel,
        name: name,
        truncate: truncate,
        target: target,
        selected: selected,
        role: role,
        download: download,
        title: title,
        onKeyDown: onKeyDown,
        truncateStringProps: truncateStringProps,
        readOnly: readOnly,
        value: value,
        iconProps: iconProps,
        children: [/*#__PURE__*/_jsx(StyledLoadingButtonContent, {
          $isLoading: isLoading || displayResult // used to trigger an addition to the a11y tree and read out the initial loading state UIComponents/#8675
          ,
          children: renderedContents
        }), /*#__PURE__*/_jsx(LoadingSpinner, Object.assign({}, spinnerProps, {
          "data-loading": null,
          failed: failed,
          use: use,
          size: size,
          resultIcon: resultIcon,
          showResult: displayResult,
          onResultDisplayFinish: this.setDisplayResult
          // used to trigger an addition to the a11y tree and read out the initial loading state UIComponents#8675
          ,
          hidden: !(isLoading || displayResult)
        }))]
      }))
    });
  }
}